<template>
  <AppShell title="Händelser">
    <template #actions>
      <p class="text-subtle">Senaste 7 dagarna</p>
    </template>

    <AppCard>
      <p v-if="isLoading">Hämtar händelser...</p>
      <p v-else-if="error" class="error-text">{{ error }}</p>

      <AppTable v-else>
        <thead>
          <tr>
            <th>Händelse</th>
            <th>Tidpunkt</th>
            <th>Källa</th>
          </tr>
        </thead>
        <tbody>
          <AppTableRow v-for="(row, index) in rows" :key="`${row.occurredAt}-${index}`">
            <AppTableCell>
              <div class="event-summary">
                <span>{{ row.label }}</span>
                <span v-if="row.detail" class="event-detail text-subtle">{{ row.detail }}</span>
              </div>
            </AppTableCell>
            <AppTableCell nowrap>{{ formatDateTimeValue(row.occurredAt) }}</AppTableCell>
            <AppTableCell nowrap>
              <AppRouterLink v-if="row.sourceLink" :to="row.sourceLink.to">
                {{ row.sourceLink.label }}
              </AppRouterLink>
              <span v-else class="text-subtle">-</span>
            </AppTableCell>
          </AppTableRow>

          <AppTableRow v-if="rows.length === 0">
            <AppTableCell colspan="3" class="empty-row text-subtle">Inga händelser de senaste 7 dagarna.</AppTableCell>
          </AppTableRow>
        </tbody>
      </AppTable>
    </AppCard>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { AppCard, AppTable, AppTableCell, AppTableRow } from '@staff/ui'
import { formatDateTimeValue } from '@staff/shared'
import AppShell from '@/components/AppShell.vue'
import AppRouterLink from '@/components/AppRouterLink.vue'
import { useMetadata } from '@/composables/useMetadata'

import { fetchDistributorEvents, type DistributorEvent } from '@/services/distributorEvents'

// Keyed by event type, since several events use the same field name for different code sets.
const detailReferences: Record<string, { field: string; reference: string }> = {
  application_status_changed: { field: 'status', reference: 'applicationStatuses' },
  kyc_status_changed: { field: 'status', reference: 'kycStatuses' },
  application_addition_requested: { field: 'addition_type', reference: 'applicationAdditionTypes' },
  invoice_created: { field: 'invoice_type', reference: 'invoiceTypes' },
}

const { translateRef } = useMetadata()

const getDetail = (event: DistributorEvent) => {
  const detail = detailReferences[event.type]
  const code = detail && event.data[detail.field]
  return typeof code === 'string' ? translateRef(detail.reference, code) : ''
}

const getSourceLink = (event: DistributorEvent) => {
  const { application_id: applicationId, debt_id: debtId } = event.data

  if (typeof applicationId === 'string') {
    return event.type === 'application_addition_requested'
      ? { label: 'Till komplettering', to: `/ansokningar/${applicationId}/kompletteringar` }
      : { label: 'Till ansökan', to: `/ansokningar/${applicationId}` }
  }
  if (typeof debtId === 'string') {
    return { label: 'Till lån', to: `/bolan/${debtId}` }
  }

  return undefined
}

const isLoading = ref(true)
const error = ref('')
const events = ref<DistributorEvent[]>([])

const rows = computed(() =>
  events.value.map((event) => ({
    occurredAt: event.occurredAt,
    label: translateRef('distributorWebhookTypes', event.type),
    detail: getDetail(event),
    sourceLink: getSourceLink(event),
  })),
)

const loadEvents = async () => {
  isLoading.value = true
  error.value = ''

  try {
    events.value = await fetchDistributorEvents()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Ett fel uppstod när händelser hämtades.'
  } finally {
    isLoading.value = false
  }
}

void loadEvents()
</script>

<style scoped lang="scss">
.event-summary {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.event-detail {
  font-size: var(--font-size-xs);
  line-height: var(--font-size-xs-line-height);
}

.empty-row {
  text-align: center;
}
</style>
